<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 mt-10">
    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" @click.stop>
      <div class="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Detalhes da Solicitação</h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <XIcon class="w-5 h-5" />
        </button>
      </div>
      
      <div class="p-6">
        <!-- Status e Tipo Badges -->
        <div class="mb-6 flex gap-3">
          <span :class="getStatusBadgeClass(recallItem?.status)" class="inline-flex px-3 py-1 text-sm font-semibold rounded-full">
            {{ getStatusText(recallItem?.status) }}
          </span>
          <span :class="getTypeBadgeClass(recallItem?.type)" class="inline-flex px-3 py-1 text-sm font-semibold rounded-full">
            {{ getTypeText(recallItem?.type) }}
          </span>
        </div>

        <!-- Informações do Paciente -->
        <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <UserIcon class="w-4 h-4 mr-2" />
            Informações do Paciente
          </h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Nome:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.patient_name }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Documento:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.patient_document }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Telefone:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.patient_phone || 'Não informado' }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Email:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.patient_email || 'Não informado' }}</p>
            </div>
          </div>
        </div>

        <!-- Informações do Exame Original -->
        <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <DocumentTextIcon class="w-4 h-4 mr-2" />
            Exame Original
          </h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Exame:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.service_name }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">OS:</p>
              <p class="font-medium text-blue-600 dark:text-blue-400">{{ recallItem?.accession_number }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Data Original:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(recallItem?.original_date) }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Horário Original:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.original_time }}</p>
            </div>
          </div>
        </div>

        <!-- Motivo da Solicitação -->
        <div class="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 mb-6">
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <ExclamationTriangleIcon class="w-4 h-4 mr-2" />
            Detalhes da Solicitação
          </h4>
          <div class="space-y-3">
            <div v-if="recallItem?.reason_type">
              <p class="text-sm text-gray-600 dark:text-gray-400">Tipo de Motivo:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ getReasonTypeText(recallItem?.reason_type) }}</p>
            </div>
            <div v-if="recallItem?.description">
              <p class="text-sm text-gray-600 dark:text-gray-400">Descrição:</p>
              <p class="text-sm text-gray-700 dark:text-gray-300">{{ recallItem?.description }}</p>
            </div>
            <div v-if="recallItem?.additional_notes">
              <p class="text-sm text-gray-600 dark:text-gray-400">Observações Adicionais:</p>
              <p class="text-sm text-gray-700 dark:text-gray-300">{{ recallItem?.additional_notes }}</p>
            </div>
            <div v-if="recallItem?.reason && !recallItem?.description">
              <p class="text-sm text-gray-600 dark:text-gray-400">Motivo:</p>
              <p class="text-sm text-gray-700 dark:text-gray-300">{{ recallItem?.reason }}</p>
            </div>
          </div>
        </div>

        <!-- Informações de Agendamento (se agendado) -->
        <div v-if="recallItem?.status === 'scheduled' && recallItem?.scheduled_date" class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mb-6">
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <CalendarIcon class="w-4 h-4 mr-2" />
            Novo Agendamento
          </h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Nova Data:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(recallItem?.scheduled_date) }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Novo Horário:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.scheduled_time }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Sala:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.room_name }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Agendado por:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.scheduled_by }}</p>
            </div>
          </div>
          <div v-if="recallItem?.schedule_notes" class="mt-3">
            <p class="text-sm text-gray-600 dark:text-gray-400">Observações:</p>
            <p class="text-sm text-gray-700 dark:text-gray-300">{{ recallItem?.schedule_notes }}</p>
          </div>
        </div>

        <!-- Informações de Conclusão (se concluído) -->
        <div v-if="recallItem?.status === 'completed'" class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mb-6">
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <CheckCircleIcon class="w-4 h-4 mr-2" />
            Conclusão
          </h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Data de Conclusão:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(recallItem?.completed_date) }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Concluído por:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.completed_by }}</p>
            </div>
          </div>
          <div v-if="recallItem?.completion_notes" class="mt-3">
            <p class="text-sm text-gray-600 dark:text-gray-400">Observações:</p>
            <p class="text-sm text-gray-700 dark:text-gray-300">{{ recallItem?.completion_notes }}</p>
          </div>
        </div>

        <!-- Informações de Cancelamento (se cancelado) -->
        <div v-if="recallItem?.status === 'cancelled'" class="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-6">
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <XCircleIcon class="w-4 h-4 mr-2" />
            Cancelamento
          </h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Data de Cancelamento:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(recallItem?.cancelled_date) }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Cancelado por:</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ recallItem?.cancelled_by }}</p>
            </div>
          </div>
          <div v-if="recallItem?.cancellation_reason" class="mt-3">
            <p class="text-sm text-gray-600 dark:text-gray-400">Motivo do Cancelamento:</p>
            <p class="text-sm text-gray-700 dark:text-gray-300">{{ recallItem?.cancellation_reason }}</p>
          </div>
        </div>

        <!-- Histórico de Atividades -->
        <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <ClockIcon class="w-4 h-4 mr-2" />
            Histórico de Atividades
          </h4>
          <div class="space-y-3">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <div class="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-gray-900 dark:text-white">Solicitação criada</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatDateTime(recallItem?.created_at) }}</p>
                <p class="text-xs text-gray-600 dark:text-gray-400">Criado por: {{ recallItem?.created_by }}</p>
              </div>
            </div>
            
            <div v-if="recallItem?.scheduled_at" class="flex items-start">
              <div class="flex-shrink-0">
                <div class="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-gray-900 dark:text-white">Solicitação agendada</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatDateTime(recallItem?.scheduled_at) }}</p>
                <p class="text-xs text-gray-600 dark:text-gray-400">Agendado por: {{ recallItem?.scheduled_by }}</p>
              </div>
            </div>
            
            <div v-if="recallItem?.completed_at" class="flex items-start">
              <div class="flex-shrink-0">
                <div class="w-2 h-2 bg-green-400 rounded-full mt-2"></div>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-gray-900 dark:text-white">Solicitação concluída</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatDateTime(recallItem?.completed_at) }}</p>
                <p class="text-xs text-gray-600 dark:text-gray-400">Concluído por: {{ recallItem?.completed_by }}</p>
              </div>
            </div>
            
            <div v-if="recallItem?.cancelled_at" class="flex items-start">
              <div class="flex-shrink-0">
                <div class="w-2 h-2 bg-red-400 rounded-full mt-2"></div>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-gray-900 dark:text-white">Solicitação cancelada</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatDateTime(recallItem?.cancelled_at) }}</p>
                <p class="text-xs text-gray-600 dark:text-gray-400">Cancelado por: {{ recallItem?.cancelled_by }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
        <button 
          type="button" 
          @click="$emit('close')" 
          class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500"
        >
          Fechar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  XIcon, 
  UserIcon, 
  DocumentTextIcon, 
  ExclamationTriangleIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/vue/24/outline'

export default {
  name: 'RecallDetailsModal',
  components: {
    XIcon,
    UserIcon,
    DocumentTextIcon,
    ExclamationTriangleIcon,
    CalendarIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon
  },
  props: {
    recallItem: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  setup() {
    const formatDate = (date) => {
      if (!date) return ''
      return new Date(date).toLocaleDateString('pt-BR')
    }

    const formatDateTime = (datetime) => {
      if (!datetime) return ''
      return new Date(datetime).toLocaleString('pt-BR')
    }

    const getStatusText = (status) => {
      const statusMap = {
        pending: 'Pendente',
        scheduled: 'Agendado',
        completed: 'Concluído',
        cancelled: 'Cancelado'
      }
      return statusMap[status] || status
    }

    const getStatusBadgeClass = (status) => {
      const classMap = {
        pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      }
      return classMap[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }

    const getTypeText = (type) => {
      const typeMap = {
        pendencia: 'Pendência',
        reconvocacao: 'Reconvocação'
      }
      return typeMap[type] || type
    }

    const getTypeBadgeClass = (type) => {
      const classMap = {
        pendencia: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
        reconvocacao: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      }
      return classMap[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }

    const getReasonTypeText = (reasonType) => {
      const reasonMap = {
        // Motivos de Pendência
        'aguardando_documentos': 'Aguardando Documentos',
        'aguardando_autorizacao': 'Aguardando Autorização',
        'aguardando_pagamento': 'Aguardando Pagamento',
        'aguardando_laudo_anterior': 'Aguardando Laudo Anterior',
        'aguardando_exame_complementar': 'Aguardando Exame Complementar',
        'aguardando_avaliacao_medica': 'Aguardando Avaliação Médica',
        'outro_pendencia': 'Outro',
        // Motivos de Reconvocação
        'complemento_exame': 'Complemento de Exame',
        'exame_inconclusivo': 'Exame Inconclusivo',
        'nova_avaliacao': 'Nova Avaliação',
        'controle': 'Controle',
        'repeticao_exame': 'Repetição de Exame',
        'exame_insatisfatorio': 'Exame Insatisfatório',
        'outro_reconvocacao': 'Outro'
      }
      return reasonMap[reasonType] || reasonType
    }

    return {
      formatDate,
      formatDateTime,
      getStatusText,
      getStatusBadgeClass,
      getTypeText,
      getTypeBadgeClass,
      getReasonTypeText
    }
  }
}
</script> 